/**
 * Data Sharing Indicator Components
 *
 * Visual indicators for showing when data is shared across organisations.
 * Supports your principle of explicit, auditable, and revocable data sharing.
 */

import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions, RequirePermission } from '../contexts/PermissionContext';
import config from '../config/environment';
import './DataSharingIndicator.css';
import Button from './ui/Button';
import { humanizeLabel } from '../services/formatting';

// Data sharing info interface
interface SharedWithOrg {
  organisationId: number;
  organisationName: string;
  accessLevel: 'read' | 'write' | 'admin';
  grantedAt: string;
  grantedBy: string;
  expiresAt?: string;
  canRevoke: boolean;
}

interface SharedFromOrg {
  organisationId: number;
  organisationName: string;
  accessLevel: 'read' | 'write' | 'admin';
}

interface DataSharingInfo {
  isShared: boolean;
  isSharedWithOthers: boolean;
  isReceivedFromOthers: boolean;
  sharedWith: SharedWithOrg[];
  sharedFrom?: SharedFromOrg;
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium' }).format(date);
}

/**
 * Get access level display
 */
function getAccessLevelDisplay(level: string): { label: string; color: string } {
  const levels: Record<string, { label: string; color: string }> = {
    read: { label: 'View Only', color: 'var(--chart-blue)' },
    write: { label: 'Can Edit', color: 'var(--chart-orange)' },
    admin: { label: 'Full Access', color: 'var(--feedback-error)' },
  };
  return levels[level] || { label: level, color: 'var(--text-secondary)' };
}

/**
 * Compact sharing badge
 * Shows a small indicator when data is shared
 */
interface SharingBadgeProps {
  resourceType: string;
  resourceId: string;
  size?: 'small' | 'medium';
  onClick?: () => void;
}

export const SharingBadge: React.FC<SharingBadgeProps> = ({
  resourceType,
  resourceId,
  size = 'small',
  onClick,
}) => {
  const [sharingInfo, setSharingInfo] = useState<DataSharingInfo | null>(null);
  const { selectedOrganisationId } = useAuth();

  useEffect(() => {
    if (!config.enableDataSharingIndicators) {
      return;
    }

    const fetchSharingInfo = async () => {
      try {
        const response = await apiClient.get<DataSharingInfo>(
          '/api/sharing/info/',
          {
            organisation_id: selectedOrganisationId || undefined,
            resource_type: resourceType,
            resource_id: resourceId,
          }
        );
        setSharingInfo(response);
      } catch {
        // Silently fail - sharing badge is non-critical
      }
    };

    fetchSharingInfo();
  }, [resourceType, resourceId, selectedOrganisationId]);

  if (!config.enableDataSharingIndicators || !sharingInfo?.isShared) {
    return null;
  }

  const isReceived = sharingInfo.isReceivedFromOthers;
  const isSharedOut = sharingInfo.isSharedWithOthers;

  return (
    <button
      className={`sharing-badge sharing-badge-${size} ${isReceived ? 'sharing-badge-received' : ''} ${isSharedOut ? 'sharing-badge-shared' : ''}`}
      onClick={onClick}
      title={
        isReceived
          ? `Shared from ${sharingInfo.sharedFrom?.organisationName}`
          : `Shared with ${sharingInfo.sharedWith.length} organisation(s)`
      }
    >
      {isReceived ? (
        <>
          <span className="sharing-badge-icon">↓</span>
          <span className="sharing-badge-text">Received</span>
        </>
      ) : (
        <>
          <span className="sharing-badge-icon">↗</span>
          <span className="sharing-badge-text">Shared</span>
        </>
      )}
    </button>
  );
};

/**
 * Inline sharing indicator
 * Shows a subtle indicator inline with content
 */
interface InlineSharingIndicatorProps {
  isShared: boolean;
  isReceived?: boolean;
  organisationName?: string;
}

export const InlineSharingIndicator: React.FC<InlineSharingIndicatorProps> = ({
  isShared,
  isReceived = false,
  organisationName,
}) => {
  if (!config.enableDataSharingIndicators || !isShared) {
    return null;
  }

  return (
    <span
      className={`inline-sharing-indicator ${isReceived ? 'received' : 'shared'}`}
      title={
        isReceived
          ? `Shared from ${organisationName || 'another organisation'}`
          : `Shared with other organisations`
      }
    >
      {isReceived ? '↓' : '↗'}
    </span>
  );
};

/**
 * Full sharing details panel
 * Shows detailed sharing information with management capabilities
 */
interface SharingDetailsPanelProps {
  resourceType: string;
  resourceId: string;
  onClose?: () => void;
}

export const SharingDetailsPanel: React.FC<SharingDetailsPanelProps> = ({
  resourceType,
  resourceId,
  onClose,
}) => {
  const [sharingInfo, setSharingInfo] = useState<DataSharingInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRevoking, setIsRevoking] = useState<number | null>(null);
  const { selectedOrganisationId } = useAuth();
  const { hasPermission } = usePermissions();

  const canManageSharing = hasPermission('manage:data_sharing');

  useEffect(() => {
    const fetchSharingInfo = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await apiClient.get<DataSharingInfo>(
          '/api/sharing/details/',
          {
            organisation_id: selectedOrganisationId || undefined,
            resource_type: resourceType,
            resource_id: resourceId,
          }
        );
        setSharingInfo(response);
      } catch (err) {
        setError('Failed to load sharing information');
        console.error('Sharing details error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSharingInfo();
  }, [selectedOrganisationId, resourceType, resourceId]);

  const handleRevoke = async (targetOrgId: number) => {
    if (!canManageSharing) return;

    setIsRevoking(targetOrgId);
    try {
      await apiClient.post('/api/sharing/revoke/', {
        organisation_id: selectedOrganisationId,
        target_organisation_id: targetOrgId,
        resource_type: resourceType,
        resource_id: resourceId,
      });

      // Refresh sharing info
      const response = await apiClient.get<DataSharingInfo>(
        '/api/sharing/details/',
        {
          organisation_id: selectedOrganisationId || undefined,
          resource_type: resourceType,
          resource_id: resourceId,
        }
      );
      setSharingInfo(response);
    } catch (err) {
      setError('Failed to revoke sharing');
      console.error('Revoke sharing error:', err);
    } finally {
      setIsRevoking(null);
    }
  };

  if (!config.enableDataSharingIndicators) {
    return null;
  }

  return (
    <div className="sharing-details-panel">
      <div className="sharing-details-header">
        <h3>Data Sharing</h3>
        {onClose && (
          <button className="sharing-details-close" onClick={onClose}>
            ×
          </button>
        )}
      </div>

      {isLoading && (
        <div className="sharing-details-loading">
          <p>Loading sharing information...</p>
        </div>
      )}

      {error && (
        <div className="sharing-details-error">
          <p>{error}</p>
        </div>
      )}

      {!isLoading && !error && sharingInfo && (
        <div className="sharing-details-content">
          {/* Received From Section */}
          {sharingInfo.sharedFrom && (
            <div className="sharing-section">
              <h4 className="sharing-section-title">
                <span className="sharing-section-icon">↓</span>
                Received From
              </h4>
              <div className="sharing-org-card received">
                <div className="sharing-org-info">
                  <span className="sharing-org-name">
                    {sharingInfo.sharedFrom.organisationName}
                  </span>
                  <span
                    className="sharing-access-badge"
                    style={{ backgroundColor: getAccessLevelDisplay(sharingInfo.sharedFrom.accessLevel).color }}
                  >
                    {getAccessLevelDisplay(sharingInfo.sharedFrom.accessLevel).label}
                  </span>
                </div>
                <p className="sharing-note">
                  This data was shared with your organisation. Any changes you make may be visible to the source organisation.
                </p>
              </div>
            </div>
          )}

          {/* Shared With Section */}
          {sharingInfo.sharedWith.length > 0 && (
            <div className="sharing-section">
              <h4 className="sharing-section-title">
                <span className="sharing-section-icon">↗</span>
                Shared With ({sharingInfo.sharedWith.length})
              </h4>
              <div className="sharing-org-list">
                {sharingInfo.sharedWith.map((org) => (
                  <div key={org.organisationId} className="sharing-org-card shared">
                    <div className="sharing-org-info">
                      <span className="sharing-org-name">{org.organisationName}</span>
                      <span
                        className="sharing-access-badge"
                        style={{ backgroundColor: getAccessLevelDisplay(org.accessLevel).color }}
                      >
                        {getAccessLevelDisplay(org.accessLevel).label}
                      </span>
                    </div>
                    <div className="sharing-org-meta">
                      <span>Granted: {formatDate(org.grantedAt)}</span>
                      {org.expiresAt && (
                        <span>Expires: {formatDate(org.expiresAt)}</span>
                      )}
                      <span>By: {org.grantedBy}</span>
                    </div>
                    {canManageSharing && org.canRevoke && (
                      <Button
                        variant="danger" size="sm"
                        onClick={() => handleRevoke(org.organisationId)}
                        disabled={isRevoking === org.organisationId}
                      >
                        {isRevoking === org.organisationId ? 'Revoking...' : 'Revoke Access'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Sharing */}
          {!sharingInfo.isShared && (
            <div className="sharing-details-empty">
              <p>This data is not currently shared with any other organisations.</p>
              <RequirePermission permission="manage:data_sharing">
                <Button variant="primary">
                  + Share with Organisation
                </Button>
              </RequirePermission>
            </div>
          )}

          {/* Privacy Notice */}
          <div className="sharing-privacy-notice">
            <p>
              <strong>Privacy:</strong> All data sharing is explicit, auditable, and revocable.
              Access logs are maintained for compliance purposes.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Organisation context indicator
 * Shows which organisation's data is currently being viewed
 */
interface OrganisationContextIndicatorProps {
  className?: string;
}

export const OrganisationContextIndicator: React.FC<OrganisationContextIndicatorProps> = ({
  className = '',
}) => {
  const { getSelectedOrganisation, user } = useAuth();
  const selectedOrg = getSelectedOrganisation();

  if (!selectedOrg || !user) {
    return null;
  }

  return (
    <div className={`org-context-indicator ${className}`}>
      <span className="org-context-icon">🏢</span>
      <span className="org-context-name">{selectedOrg.name}</span>
      <span className="org-context-role">({humanizeLabel(selectedOrg.role)})</span>
    </div>
  );
};

export default SharingBadge;
